/**
 * src/context/HarperWSContext.tsx — Shared HarperDB WebSocket connection.
 *
 * Maintains a single WebSocket per authenticated session. Components subscribe
 * to specific tables via useHarperSubscription(). When any record in a subscribed
 * table changes, the callback fires with the updated record.
 *
 * Connection URL: wss://<serving-host>/{TableName} — host is derived from
 * HARPER_BASE_URL, which defaults to window.location.origin so the WS
 * follows whichever hostname Harper served the SPA from.
 * Auth: Basic auth (same credentials as REST).
 * One WebSocket per table subscription.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { HARPER_BASE_URL } from '../api/harperClient';
import { useAuth } from './AuthContext';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type MessageHandler = (data: unknown) => void;

interface Subscription {
  table: string;
  handler: MessageHandler;
  id: number;
}

/**
 * A captured WS message entry. Kept in a short rolling buffer and attached to
 * feedback + error payloads so the admin can see what the client had just
 * received when a bug was reported.
 */
export interface WsLogEntry {
  ts: string;              // ISO timestamp
  table: string;
  data: unknown;
}

const WS_LOG_CAP = 20;

/**
 * Heartbeat interval. Browsers and intermediate NATs (especially mobile LTE)
 * silently drop idle TCP connections after ~30-60s. Sending an application-
 * level ping every 25s keeps the socket warm; if the underlying connection is
 * dead, ws.send throws or onclose fires and the existing reconnect logic
 * takes over.
 */
const HEARTBEAT_MS = 25_000;

/**
 * Reconnect backoff. Linear 3-5s jitter (the previous behavior) hammered the
 * server during extended outages. Exponential with a 60s ceiling lets short
 * blips reconnect fast while longer outages stop wasting battery + bandwidth.
 */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 60_000;
const RECONNECT_JITTER_MS = 1_000;

/**
 * How long a socket must STAY open before we call the connection healthy and
 * reset the backoff.
 *
 * Resetting on `onopen` alone is what made the backoff decorative: Harper
 * accepts the upgrade (the browser logs a 101) and only then rejects the
 * token, closing ~40ms later. Every cycle therefore reset the counter to zero,
 * so a permanently-failing table reconnected every ~1s forever instead of
 * backing off to 60s — two tables on the customer dashboard meant ~2 sockets
 * per second, per client, indefinitely. Measured against staging 2026-07-30.
 */
const STABLE_CONNECTION_MS = 10_000;

interface HarperWSContextValue {
  subscribe: (table: string, handler: MessageHandler) => number;
  unsubscribe: (id: number) => void;
  connected: boolean;
  /** Returns a snapshot of the last N WS messages (newest last). */
  getRecentMessages: () => WsLogEntry[];
}

const HarperWSCtx = createContext<HarperWSContextValue>({
  subscribe: () => 0,
  unsubscribe: () => {},
  connected: false,
  getRecentMessages: () => [],
});

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getWsUrl(table: string): string {
  // Convert https:// to wss://
  const base = HARPER_BASE_URL.replace(/^http/, 'ws');
  return `${base}/${table}`;
}

/**
 * Is this close permanent — i.e. would reconnecting be pointless?
 *
 * Deliberately narrow. It matches only Harper's "nothing serves this path"
 * close, because a false positive here disables live updates for a table until
 * the next full reload. Anything ambiguous (a bare 1011, a network drop) must
 * keep retrying.
 */
function isPermanentCloseReason(event: CloseEvent): boolean {
  // Nothing serves this path — only @export tables are routable.
  if (event.code === 1011 && /no resource was found/i.test(event.reason ?? '')) return true;
  // The server took the upgrade and then refused the token. Retrying with the
  // SAME token cannot succeed, and the token is fixed for the life of the
  // session — so this is permanent in every sense that matters here. Harper
  // sends `3000 "Error: Unauthorized"` for the WS subscribe path today; web
  // clients have been looping on it (see STABLE_CONNECTION_MS).
  if (event.code === 3000 && /unauthorized/i.test(event.reason ?? '')) return true;
  return false;
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

let nextSubId = 1;

export function HarperWSProvider({ children }: { children: React.ReactNode }) {
  const { customerEmail, getAccessToken } = useAuth();
  const subsRef = useRef<Subscription[]>([]);
  const socketsRef = useRef<Map<string, WebSocket>>(new Map());
  const reconnectTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const reconnectAttemptsRef = useRef<Map<string, number>>(new Map());
  const heartbeatTimersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  // Pending 'this connection has stayed up' timers — see STABLE_CONNECTION_MS.
  const stableTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Tables the server has told us it does not serve. Remembered for the life of
  // the session so a later-mounting subscriber doesn't restart the same futile
  // connect/close cycle.
  const deadTablesRef = useRef<Set<string>>(new Set());
  const [connected, setConnected] = useState(false);
  // Rolling buffer of the last WS_LOG_CAP parsed messages. Lives on a ref so
  // pushes don't re-render the tree — consumers read it imperatively via
  // getRecentMessages() only at the moment they submit feedback / capture an error.
  const wsLogRef = useRef<WsLogEntry[]>([]);

  const connectTable = useCallback((table: string) => {
    if (socketsRef.current.has(table)) return; // already connected
    if (deadTablesRef.current.has(table)) return; // server does not serve it
    const token = getAccessToken();
    if (!customerEmail || !token) return;

    const wsUrl = getWsUrl(table);

    // Browser WebSocket can't set custom headers, so auth goes in the
    // Sec-WebSocket-Protocol subprotocol. Harper accepts a bearer.<token>
    // subprotocol and validates the JWT the same way REST Bearer does.
    // Tokens contain `.` which is not a valid subprotocol character, so we
    // pass a base64-url-encoded form.
    const encodedToken = btoa(token).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl, [`bearer.${encodedToken}`]);
    } catch {
      // Fallback: plain connection
      ws = new WebSocket(wsUrl);
    }

    socketsRef.current.set(table, ws);

    ws.onopen = () => {
      setConnected(true);
      // Reset the backoff only once the socket has STAYED open — see
      // STABLE_CONNECTION_MS. Resetting here, on open alone, is what let a
      // server that accepts the upgrade and then closes defeat the backoff
      // entirely. onclose clears this timer, so a short-lived socket never
      // earns a reset and the delay keeps doubling toward the 60s ceiling.
      const priorStable = stableTimersRef.current.get(table);
      if (priorStable) clearTimeout(priorStable);
      stableTimersRef.current.set(
        table,
        setTimeout(() => {
          reconnectAttemptsRef.current.delete(table);
          stableTimersRef.current.delete(table);
        }, STABLE_CONNECTION_MS),
      );
      // Clear any reconnect timer
      const timer = reconnectTimersRef.current.get(table);
      if (timer) { clearTimeout(timer); reconnectTimersRef.current.delete(table); }
      // Start heartbeat. Sending a ping every HEARTBEAT_MS keeps mobile/NAT
      // intermediaries from dropping the idle TCP connection. If the socket
      // is already dead, ws.send throws and onclose fires (caught below).
      const existingHb = heartbeatTimersRef.current.get(table);
      if (existingHb) clearInterval(existingHb);
      const hb = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        try { ws.send(JSON.stringify({ type: 'ping', ts: Date.now() })); }
        catch { try { ws.close(); } catch { /* noop */ } }
      }, HEARTBEAT_MS);
      heartbeatTimersRef.current.set(table, hb);
    };

    ws.onmessage = (event) => {
      try {
        const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        // Push into the rolling log for feedback/error context, capped at WS_LOG_CAP.
        wsLogRef.current.push({ ts: new Date().toISOString(), table, data: msg });
        if (wsLogRef.current.length > WS_LOG_CAP) {
          wsLogRef.current.splice(0, wsLogRef.current.length - WS_LOG_CAP);
        }
        // Notify all subscribers for this table
        for (const sub of subsRef.current) {
          if (sub.table === table) {
            try { sub.handler(msg); } catch { /* swallow handler errors */ }
          }
        }
      } catch { /* ignore unparseable messages */ }
    };

    ws.onerror = () => {
      // Will trigger onclose
    };

    ws.onclose = (event) => {
      socketsRef.current.delete(table);
      setConnected(socketsRef.current.size > 0);
      // Closed before it was judged stable → no backoff reset for this attempt.
      const stable = stableTimersRef.current.get(table);
      if (stable) { clearTimeout(stable); stableTimersRef.current.delete(table); }
      // Stop the heartbeat for this table — onopen will start a fresh one
      // on the next successful connection.
      const hb = heartbeatTimersRef.current.get(table);
      if (hb) { clearInterval(hb); heartbeatTimersRef.current.delete(table); }

      // Some closes are PERMANENT and retrying is pure waste. Harper closes
      // with 1011 + "No resource was found to handle /X" when nothing serves
      // that path — only tables marked @export in schema.graphql are routable,
      // and several names we subscribe to (e.g. Shift) are not. Retrying that
      // can never succeed, so without this check a driver's phone reconnects to
      // a nonexistent endpoint for the whole shift, burning battery and radio.
      // Verified in tests/integration/RealtimeSubscriptions.test.ts.
      if (isPermanentCloseReason(event)) {
        deadTablesRef.current.add(table);
        console.warn(
          `[HarperWS] no server-side subscription for "${table}" (code ${event.code}: ${event.reason}). ` +
          'Not reconnecting. Live updates for this table are DISABLED — the UI will only refresh ' +
          'on visibilitychange/pageshow or an explicit refetch.',
        );
        return;
      }

      // Auto-reconnect if there are still subscribers for this table.
      // Exponential backoff with a 60s ceiling: short blips reconnect in
      // ~1s, long outages stop hammering after a minute. Counter is reset
      // on the next successful onopen.
      const hasSubscribers = subsRef.current.some((s) => s.table === table);
      if (hasSubscribers && customerEmail) {
        const attempt = reconnectAttemptsRef.current.get(table) ?? 0;
        reconnectAttemptsRef.current.set(table, attempt + 1);
        const expDelay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
        const delay = expDelay + Math.random() * RECONNECT_JITTER_MS;
        const timer = setTimeout(() => {
          reconnectTimersRef.current.delete(table);
          connectTable(table);
        }, delay);
        reconnectTimersRef.current.set(table, timer);
      }
    };
  }, [customerEmail, getAccessToken]);

  // Clean up all connections on unmount or auth change.
  // Snapshot refs into locals so the cleanup closes over the Map instance
  // we observed when the effect ran, not whatever ref.current points to at
  // unmount time (react-hooks/exhaustive-deps).
  useEffect(() => {
    const sockets = socketsRef.current;
    const reconnectTimers = reconnectTimersRef.current;
    const heartbeatTimers = heartbeatTimersRef.current;
    const reconnectAttempts = reconnectAttemptsRef.current;
    const stableTimers = stableTimersRef.current;
    return () => {
      for (const ws of sockets.values()) {
        try { ws.close(); } catch { /* noop */ }
      }
      sockets.clear();
      for (const timer of reconnectTimers.values()) clearTimeout(timer);
      reconnectTimers.clear();
      for (const hb of heartbeatTimers.values()) clearInterval(hb);
      heartbeatTimers.clear();
      for (const st of stableTimers.values()) clearTimeout(st);
      stableTimers.clear();
      reconnectAttempts.clear();
    };
  }, [customerEmail]);

  const subscribe = useCallback((table: string, handler: MessageHandler): number => {
    const id = nextSubId++;
    subsRef.current.push({ table, handler, id });
    // Connect to this table if not already connected
    connectTable(table);
    return id;
  }, [connectTable]);

  const unsubscribe = useCallback((id: number) => {
    subsRef.current = subsRef.current.filter((s) => s.id !== id);
    // If no more subscribers for a table, close its socket and tear down
    // its heartbeat + pending reconnect.
    const activeTables = new Set(subsRef.current.map((s) => s.table));
    for (const [table, ws] of socketsRef.current.entries()) {
      if (!activeTables.has(table)) {
        ws.close();
        socketsRef.current.delete(table);
        const hb = heartbeatTimersRef.current.get(table);
        if (hb) { clearInterval(hb); heartbeatTimersRef.current.delete(table); }
        const rc = reconnectTimersRef.current.get(table);
        if (rc) { clearTimeout(rc); reconnectTimersRef.current.delete(table); }
        reconnectAttemptsRef.current.delete(table);
      }
    }
  }, []);

  const getRecentMessages = useCallback((): WsLogEntry[] => {
    return wsLogRef.current.slice();
  }, []);

  return (
    <HarperWSCtx.Provider value={{ subscribe, unsubscribe, connected, getRecentMessages }}>
      {children}
    </HarperWSCtx.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Subscribe to real-time changes on a Harper table.
 * When any record in the table changes, onMessage fires with the update.
 */
export function useHarperSubscription(
  table: string | null,
  onMessage: (data: unknown) => void,
): { connected: boolean } {
  const { subscribe, unsubscribe, connected } = useContext(HarperWSCtx);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!table) return;
    const id = subscribe(table, (data) => handlerRef.current(data));
    return () => unsubscribe(id);
  }, [table, subscribe, unsubscribe]);

  return { connected };
}

/**
 * Read-only accessor for the rolling WS message log. Used by the feedback
 * widget + client error reporter to attach recent context to their payloads.
 */
export function useHarperWsLog(): () => WsLogEntry[] {
  const { getRecentMessages } = useContext(HarperWSCtx);
  return getRecentMessages;
}
